/**
 * Centralized API Configuration
 *
 * This file provides the base URLs for all API calls.
 *
 * Development (localhost:3000): Uses environment variables (current approach)
 * Production: Automatically derives from browser's current origin
 */

/**
 * Check if we're running in development (localhost:3000)
 */
const isDevelopment = (): boolean => {
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    return (
      origin.includes("localhost:3000") ||
      origin.includes("localhost:3001") ||
      origin.includes("127.0.0.1:3000") ||
      origin.includes("https://zournal-dev.notionpress.in") ||
      origin.includes("https://zournal-1.notionpress.in")
    );
  }
  // Fallback: check NODE_ENV if window is not available
  return process.env.NODE_ENV === "development";
};

/**
 * Get the base URL for API calls
 */
const getBaseUrl = (): string => {
  if (isDevelopment()) {
    // In development, use environment variables (current approach)
    return (
      process.env.REACT_APP_API_SERVER_END_POINT || "http://localhost:8081"
    );
  }

  // In production, use browser's origin
  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  // Fallback
  return process.env.REACT_APP_API_SERVER_END_POINT || "http://localhost:8081";
};

/**
 * Get the core API URL
 */
const getCoreApiUrl = (): string => {
  if (isDevelopment()) {
    // In development, use the local core service via REACT_APP_CORE_API_URL
    // (defaults to the local cockpit-core container).
    // return process.env.REACT_APP_CORE_API_URL || "http://localhost:8082";
    return "https://dev.initium.zournalhq.com/core";
  }

  // In production, use browser's origin + /core
  if (typeof window !== "undefined") {
    return `${window.location.origin}/core`;
  }

  // Fallback
  return process.env.REACT_APP_CORE_API_URL || "";
};

const BASE_URL = getBaseUrl();

/**
 * Backend API Base URL
 *
 * Development: Uses REACT_APP_API_SERVER_END_POINT from .env
 * Production: Uses browser origin + /backend
 * Example: https://client-1.zournal.in/backend
 */
export const API_BASE_URL = isDevelopment()
  ? BASE_URL // In dev, BASE_URL already contains the full backend URL
  : `${BASE_URL}/backend`;

/**
 * Core API Base URL
 *
 * Development: Uses REACT_APP_CORE_API_URL from .env
 * Production: Uses browser origin + /core
 * Example: https://client-1.zournal.in/core
 */
export const CORE_API_URL = getCoreApiUrl();
